import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FullWidthContainer, PageLoader } from '../../components';
import { Button } from '../../components/Button';
import { Select } from '../../components/forms/Select';
import { useSchedulingAccess, useSchedulingProfiles } from '../Calendars/scheduling/hooks';
import { CalendarTab } from '../Calendars/scheduling/tabs/CalendarTab';
import { BookingsTab } from './BookingsTab';

const LAST_PROFILE_KEY = 'appointments:profile';
const TABS = ['calendar', 'bookings'] as const;
type TabId = (typeof TABS)[number];

// Daily work view of the scheduling calendars: calendar and booking feed, setup is one click away.
export const Appointments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profileId } = useParams<{ profileId?: string }>();
  const { data: access, isLoading: accessLoading } = useSchedulingAccess();
  const enabled = access?.enabled ?? false;
  const { data: profiles = [], isLoading: profilesLoading } = useSchedulingProfiles(enabled);
  const [tab, setTab] = useState<TabId>('calendar');

  const current = profiles.find(profile => profile.id === profileId);

  useEffect(() => {
    if (current) localStorage.setItem(LAST_PROFILE_KEY, current.id);
  }, [current]);

  if (accessLoading || (enabled && profilesLoading)) return <PageLoader show />;
  if (!enabled) return <Navigate to="/calendars" replace />;

  const title = (
    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
      {t('navigation.appointments')}
    </h1>
  );

  if (profiles.length === 0) {
    return (
      <FullWidthContainer>
        <div className="space-y-4">
          {title}
          <p className="text-gray-600 dark:text-gray-400">{t('appointments.noCalendar')}</p>
          <Button onClick={() => navigate('/calendars?area=scheduling')}>
            {t('appointments.createCalendar')}
          </Button>
        </div>
      </FullWidthContainer>
    );
  }

  // Without a valid id in the URL, open the last used calendar (or the first active one).
  if (!current) {
    const lastId = localStorage.getItem(LAST_PROFILE_KEY);
    const target =
      profiles.find(profile => profile.id === lastId) ??
      profiles.find(profile => profile.is_active) ??
      profiles[0];
    return <Navigate to={`/appointments/${target.id}`} replace />;
  }

  const options = profiles.map(profile => ({
    value: profile.id,
    label: profile.is_active ? profile.name : `${profile.name} (${t('common.inactive')})`,
  }));

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-4">
          {title}
          {profiles.length > 1 ? (
            <div className="w-64">
              <Select
                options={options}
                value={current.id}
                onChange={value => navigate(`/appointments/${value as string}`)}
              />
            </div>
          ) : (
            <span className="text-lg text-gray-600 dark:text-gray-400">{current.name}</span>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            onClick={() => navigate(`/calendars/${current.id}`)}
          >
            {t('appointments.setup')}
          </Button>
        </div>

        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex gap-6">
            {TABS.map(tabId => (
              <button
                key={tabId}
                type="button"
                onClick={() => setTab(tabId)}
                className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${
                  tab === tabId
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {t(`appointments.tabs.${tabId}`)}
              </button>
            ))}
          </nav>
        </div>

        {tab === 'calendar' && <CalendarTab key={current.id} profile={current} />}
        {tab === 'bookings' && <BookingsTab key={current.id} profile={current} />}
      </div>
    </FullWidthContainer>
  );
};
